/* eslint-disable no-redeclare */
import { IssueType } from "@protos/issues/v1/issues";

import { useCommonIssuesFilterState } from "./useCommonIssuesFilterState";
import { useSastIssuesFilterState } from "./useSastIssuesFilterState";
import { useScaIssuesFilterState } from "./useScaIssuesFilterState";
import { useSecretsIssuesFilterState } from "./useSecretsIssuesFilterState";

// These overloads help provide excellent type hints. For example:
//
// function foo(issueType: IssueType) {
//    const a = useIssuesFilterState(issueType);       // a is a `CommonIssueFilterState` since the value of `issueType` is unknown at compile time
//    const b = useIssuesFilterState(IssueType.SAST);  // b is a `SastIssueFilterState`
//    const c = useIssuesFilterState(IssueType.SCA);   // c is a `ScaIssueFilterState`
// };

export type CommonIssueFilterState = ReturnType<
  typeof useCommonIssuesFilterState
>;
export type SastIssueFilterState = ReturnType<typeof useSastIssuesFilterState>;
export type ScaIssueFilterState = ReturnType<typeof useScaIssuesFilterState>;
export type SecretsIssueFilterState = ReturnType<
  typeof useSecretsIssuesFilterState
>;
export type IssueFilterState =
  | SastIssueFilterState
  | ScaIssueFilterState
  | SecretsIssueFilterState;

export type FilterState<T extends IssueType> = T extends IssueType.SAST
  ? SastIssueFilterState
  : T extends IssueType.SCA
  ? ScaIssueFilterState
  : T extends IssueType.SECRETS
  ? SecretsIssueFilterState
  : never;

export function useIssuesFilterState(
  issueType: IssueType.SAST
): SastIssueFilterState;
export function useIssuesFilterState(
  issueType: IssueType.SCA
): ScaIssueFilterState;
export function useIssuesFilterState(
  issueType: IssueType.SECRETS
): SecretsIssueFilterState;

export function useIssuesFilterState(issueType: IssueType.RESEARCH): never;
export function useIssuesFilterState(issueType: IssueType.UNSPECIFIED): never;

export function useIssuesFilterState(
  issueType: IssueType
): CommonIssueFilterState;

/**
 * Generates and manages state for issue filters.
 */
export function useIssuesFilterState(issueType: IssueType): IssueFilterState {
  const sastFindingFilters = useSastIssuesFilterState();
  const scaFindingFilters = useScaIssuesFilterState();
  const secretsFindingFilters = useSecretsIssuesFilterState();

  if (issueType === IssueType.SAST) {
    return sastFindingFilters;
  } else if (issueType === IssueType.SCA) {
    return scaFindingFilters;
  } else if (issueType === IssueType.SECRETS) {
    return secretsFindingFilters;
  } else {
    throw new Error(`Unsupported issue type: ${issueType}`);
  }
}
